import { useEffect, useMemo, useRef, useState } from 'react';

import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import SearchBarIcon from '@/shared-components/icons/components/forms/SearchBarIcon';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import PopupToBottomSheet from '@/shared-components/popups/components/PopupToBottomSheet';
import Text from '@/shared-components/text/components/Text';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import StickerCropper from './StickerCropper';

import './styles/CreateStickersPopup.css';

export default function CreateStickersPopup({ open, close, onConfirmSticker }) {
  // Step states for the multi-step popup
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showStickerCropper, setShowStickerCropper] = useState(false);
  const [showStickerConfirmation, setShowStickerConfirmation] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [croppedStickerData, setCroppedStickerData] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // New state for custom uploaded image source
  const [customSource, setCustomSource] = useState(null);
  const fileInputRef = useRef(null);

  const filteredArtworks = useMemo(() => {
    const term = (searchValue || '').trim().toLowerCase();
    if (!term) return artworks;
    return artworks.filter((artwork) => {
      const title = artwork?.artwork_about?.artwork_title || '';
      const resolvedArtist = getArtworkArtistDisplayName(artwork);
      const artist = resolvedArtist === '-' ? '' : resolvedArtist;
      return title.toLowerCase().includes(term) || artist.toLowerCase().includes(term);
    });
  }, [artworks, searchValue]);

  // Fetch artworks when popup opens (step 1)
  useEffect(() => {
    if (open && !showStickerCropper && !showStickerConfirmation) {
      const fetchArtworks = async () => {
        setLoading(true);
        try {
          const response = await fetchWrapper.get(
            `${config.apis.api.url}/inventory/available`,
            { injectToken: true }
          );
          const artworksData = response.data.data || response.data;
          setArtworks(artworksData);
        } catch (error) {
          console.error('Error fetching available artworks', error);
        } finally {
          setLoading(false);
        }
      };
      fetchArtworks();
    }
  }, [open, showStickerCropper, showStickerConfirmation]);

  // Handle artwork click: clear any custom image and open cropper
  const handleArtworkClick = (artwork) => {
    setSelectedArtwork(artwork);
    setCustomSource(null);
    setShowStickerCropper(true);
  };

  // Handle file input change for custom upload
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setCustomSource(imageUrl);
      setSelectedArtwork(null);
      setShowStickerCropper(true);
    }
  };

  // Trigger the file input click when custom tile is clicked
  const handleCustomUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // When cropping is done, pass result to next step
  const handleCropNext = async ({ croppedSticker, cropShape }) => {
    setCroppedStickerData({ croppedSticker, cropShape });
    setShowStickerCropper(false);
    setShowStickerConfirmation(true);
  };

  // Confirm final sticker, call parent's onConfirmSticker callback
  const handleConfirmSticker = () => {
    if (onConfirmSticker) {
      onConfirmSticker(croppedStickerData);
    }
    setShowStickerConfirmation(false);
    close();
  };

  // Render step header text based on state
  const renderStepText = () => {
    if (showStickerConfirmation) {
      return (
        <>
          <span className="create-stickers-popup__header-step-text-span">3. </span>
          Confirm the creation of your new sticker
        </>
      );
    }
    if (showStickerCropper) {
      return (
        <>
          <span className="create-stickers-popup__header-step-text-span">2. </span>
          Crop the sticker
        </>
      );
    }
    return (
      <>
        <span className="create-stickers-popup__header-step-text-span">1. </span>
        Choose from your available artworks or add a new image
      </>
    );
  };

  const renderStepOneHeader = () => (
    <div className="create-stickers-popup__header create-stickers-popup__header--first-step">
      <button
        type="button"
        className="create-stickers-popup__add-image-button"
        onClick={handleCustomUploadClick}
      >
        Add image
      </button>
      <Text className="create-stickers-popup__header-text font-size-large">
        Create Sticker
      </Text>
      <XIcon
        className="create-stickers-popup__close-button"
        onClick={close}
      />
    </div>
  );

  const renderStepperHeader = () => (
    <div className="create-stickers-popup__header">
      <div className="create-stickers-popup__header-content">
        {(showStickerCropper || showStickerConfirmation) && (
          <BackArrowIcon
            className="create-stickers-popup__go-back"
            onClick={() => {
              if (showStickerConfirmation) {
                setShowStickerConfirmation(false);
                setShowStickerCropper(true);
              } else {
                setShowStickerCropper(false);
              }
            }}
          />
        )}
        <Text className="create-stickers-popup__header-text font-size-large">
          Sticker maker
        </Text>
        <p className="create-stickers-popup__header-step-text">
          {renderStepText()}
        </p>
      </div>
      <XIcon
        className="create-stickers-popup__close-button"
        onClick={close}
      />
    </div>
  );

  const renderArtworkRow = (artwork) => {
    const title = artwork?.artwork_about?.artwork_title || 'Untitled';
    const resolvedArtist = getArtworkArtistDisplayName(artwork);
    const artist = resolvedArtist === '-' ? '' : resolvedArtist;
    return (
      <button
        key={artwork._id}
        type="button"
        className="create-stickers-popup__artwork-row"
        onClick={() => {
          setCustomSource(null);
          handleArtworkClick(artwork);
        }}
      >
        <div className="create-stickers-popup__artwork-row-info">
          <ArtworkImage
            image={artwork?.artwork_media?.artwork_main_picture?.image_id || ''}
            imageClassName="create-stickers-popup__artwork-row-image"
            imageTemplateClassName="create-stickers-popup__artwork-row-image"
          />
          <div className="create-stickers-popup__artwork-row-text">
            <p className="create-stickers-popup__artwork-row-title">{title}</p>
            <p className="create-stickers-popup__artwork-row-artist">
              {artist ? `By ${artist}` : ''}
            </p>
          </div>
        </div>
      </button>
    );
  };

  return (
    <PopupToBottomSheet
      className="create-stickers-popup-bottom-sheet__container"
      open={open}
      close={close}
      popupOptions={{ opacity: true }}
    >
      <div className="create-stickers-popup__content-container">
        {showStickerCropper || showStickerConfirmation ? renderStepperHeader() : renderStepOneHeader()}

        {/* Step content */}
        {showStickerConfirmation ? (
          // Step 3: Confirm sticker
          (<div className="create-stickers-popup__confirmation-container">
            <div className="create-stickers-popup__confirmation-preview">
              {croppedStickerData?.croppedSticker ? (
                <img
                  src={URL.createObjectURL(croppedStickerData.croppedSticker)}
                  alt="Cropped Sticker Preview"
                  className={`create-stickers-popup__confirmation-image ${
                    croppedStickerData.cropShape === 'circle'
                      ? 'confirmation-image--circle'
                      : ''
                  }`}
                />
              ) : (
                <Text extraSmall>No sticker data available</Text>
              )}
            </div>
            <div className="create-stickers-popup__confirmation-footer">
              <ContinueButton onClick={handleConfirmSticker}>
                Confirm sticker
              </ContinueButton>
            </div>
          </div>)
        ) : showStickerCropper ? (
          // Step 2: Crop sticker, using custom image if available
          (<StickerCropper
            onNext={handleCropNext}
            source={
              customSource ||
              (selectedArtwork?.artwork_media?.artwork_main_picture?.image_id || '')
            }
          />)
        ) : loading ? (
          // Step 1: Loading artworks
          <div className="create-stickers-popup__loading-state">
            <Loader thin />
          </div>
        ) : (
          // Step 1: Search + list layout
          (<>
            <div className="create-stickers-popup__search-bar">
              <SearchBarIcon className="create-stickers-popup__search-icon" />
              <input
                type="text"
                className="create-stickers-popup__search-input"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search artworks"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <div className="create-stickers-popup__artworks-container">
              {filteredArtworks?.length > 0 ? (
                <div className="create-stickers-popup__artworks-list remove-scrollbar">
                  {filteredArtworks.map(renderArtworkRow)}
                </div>
              ) : (
                <Text className="create-stickers-popup__empty-text" extraSmall>
                  No artworks found in your inventory.
                </Text>
              )}
            </div>
          </>)
        )}
      </div>
      {/* Hidden file input for custom image upload */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </PopupToBottomSheet>
  );
}
